import './RangeField.scss'

import React from 'react'

import { FormField } from '../FormField'
import { MinimalButton } from '../MinimalButton'
import { Range } from '../Range'

/**
 * Fully featured form field wrapping a range element.
 * @param {Object} props
 * @param {string} [props.className] Additional classes
 * @param {(value: [number, number], name: string) => void} [props.onChange] Change handler
 * @param {string} props.name Form element name
 * @param {string} props.label Label text of the form field
 * @param {string} [props.icon] Name of the icon to display in the label
 * @param {number} [props.step=1] Amount of one step
 * @param {number} props.min Minimum value
 * @param {number} props.max Maximum value
 * @param {[number, number]} props.value Array of values
 * @param {number[]} [props.scale] Map values to an evenly distributed scale, must be between min & max and ascending
 * @param {string} [props.unit] Unit to show in the scale
 * @param {boolean} [props.infinite] Show a "+" in the max label
 */
const RangeField = ({
    className, value, min, max, name, label, icon, unit, scale, step, onChange, infinite,
}) => {
    const rangeProps = {
        min,
        max,
        name,
        value,
        onChange,
        step: step || 1,
    }

    // Do we have a scale to apply?
    if (scale) {
        // Prepare external scale (from props) and internal scale (linear) as arrays with
        // values dividing segments (including min & max)
        const externalScale = [min].concat(scale).concat([max])
        const stepsPerSegment = (max - min) / (externalScale.length - 1)
        const internalScale = [min].concat(
            Array.from(scale.keys()).map(i => (i + 1) * stepsPerSegment),
        ).concat([max])

        /**
         * Maps from one scale to the other
         * @param {[number, number]} toMap Original value
         * @param {number[]} fromScale Scale of the original value
         * @param {number[]} toScale Value will be converted to this scale
         * @returns {[number, number]} Value converted to the new scale
         */
        const mapBetweenScales = (toMap, fromScale, toScale) => {
            // Define what we already know
            const mapped = [
                (toMap[0] === min) ? min : null,
                (toMap[1] === max) ? max : null,
            ]

            // Loop through the segments as long as necessary
            for (let i = 0; i < externalScale.length; i++) {
                // Do we already have both values?
                if (mapped[0] !== null && mapped[1] !== null) {
                    // Then stop right now and return rounded, converted values
                    return /** @type {[number, number]} */ (mapped.map(Math.round))
                }

                // Continue if we have nothing to do in this segment
                const minInSegment = mapped[0] === null && toMap[0] < fromScale[i + 1]
                const maxInSegment = mapped[1] === null && toMap[1] < fromScale[i + 1]
                if (!minInSegment && !maxInSegment) continue

                // Determine sizes if this segment in each scale
                const fromSegment = fromScale[i + 1] - fromScale[i]
                const toSegment = toScale[i + 1] - toScale[i]

                // Now we can convert values that are in the segment
                if (minInSegment) {
                    mapped[0] = toScale[i] + (toMap[0] - fromScale[i]) * toSegment / fromSegment
                }
                if (maxInSegment) {
                    mapped[1] = toScale[i] + (toMap[1] - fromScale[i]) * toSegment / fromSegment
                }
            }

            // We looped through all segments but couldn't find min and/or max
            throw Error('Please check RangeField props!')
        }

        // Convert value to the internal scale
        rangeProps.value = mapBetweenScales(value, externalScale, internalScale)

        // Convert changed values back to the external scale
        rangeProps.onChange = value => {
            return onChange(mapBetweenScales(value, internalScale, externalScale), name)
        }
    }

    const renderElement = ({ id }) => (
        <>
            <Range id={id} {...rangeProps} />
            <ul className="range-field-scale">
                <li>{min} {unit}</li>
                {scale && scale.map((value, i) => <li key={i}>{value} {unit}</li>)}
                <li>{max}{infinite && '+'} {unit}</li>
            </ul>
            <div className="range-field-values">
                <small>
                    {value[0]} {unit} - {value[1]}{value[1] === max && infinite && '+'} {unit}
                </small>
                {(value[0] !== min || value[1] !== max) && (
                    <MinimalButton icon="i-cancel" variant="text" onClick={() => onChange([min, max], name)} />
                )}
            </div>
        </>
    )

    const fieldProps = {
        className: 'range-field' + (className ? ' ' + className : ''),
        name,
        label,
        icon,
        renderElement,
    }

    return <FormField {...fieldProps} />
}

export default RangeField
